import tinycolor from 'tinycolor2'
import { getRawMessage } from '../../../locale'

const omitLinksInForm = (formValues) => {
  const linkValues = ['logo', 'background_image', 'thumbnail']
  linkValues.forEach((link) => {
    if (
      [link] in formValues &&
      typeof formValues[link] !== 'object' &&
      formValues[link] !== null
    ) {
      delete formValues[link]
    }
  })
  return formValues
}

const encodeImageFileAsUrl = (image) => {
  return new Promise((resolve) => {
    // if the image is file input
    if (image && typeof image !== 'string' && image.name) {
      const file = image
      const reader = new FileReader()
      reader.onload = (e) => {
        resolve(e.target.result)
      }
      reader.readAsDataURL(file)
    } else {
      // if there is no image or the image has already been set to URL
      resolve(image)
    }
  })
}

const applyStylesToStyleSheet = (styleSheet, cssRules) => {
  for (let index = 0; index < cssRules.length; index++) {
    styleSheet.insertRule(cssRules[index], styleSheet.cssRules.length)
  }
}

const addGeneralFont = (styleSheet, fontName) => {
  if (!styleSheet) return

  const rules = [
    `#main-form{
      font-family: '${fontName}';
    }`,
    `#main-form *{
      font-family: '${fontName}';
    }`,
  ]
  applyStylesToStyleSheet(styleSheet, rules)
}

const addGeneralTheme = (styleSheet, rgbaColor, fontName) => {
  if (!styleSheet) return

  const rules = [
    `#main-form {
      background-color: ${rgbaColor || 'rgba(255,255,255,1)'};
      transition: all 0.2s linear
    }`,
    `#main-form .dashboard-formz-form-row .toolbar-class  {
      background: ${rgbaColor};
    }`,
    `#main-form .form-maker-form-fields .formz-field-disabled::after {
      background-color: ${rgbaColor};
    }`,
    `#main-form .form-maker-form-fields .formz-field-disabled-message::after {
      content: "${getRawMessage('pages.formMaker.field.widget.notSupported')}";
    }`,
  ]
  applyStylesToStyleSheet(styleSheet, rules)
}
const addBackgroundImage = async (styleSheet, image) => {
  if (!styleSheet) return
  let img = image
  await encodeImageFileAsUrl(image).then((res) => {
    img = res
  })
  const rules = [
    `#main-form {
      background-image: url("${img}");
      background-attachment: fixed;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    }`,
    `#main-form::after {
      opacity: ${!image ? '0' : '0.5'}
    }`,
  ]
  applyStylesToStyleSheet(styleSheet, rules)
}

const addBackgroundShadow = (styleSheet, rgbaColor = 'rgba(0,0,0,0.0)') => {
  if (!styleSheet) return
  const color = tinycolor(rgbaColor)

  const rules = [
    `#main-form::after {
      background-color: ${color.toHex8String()}
    }`,
  ]
  applyStylesToStyleSheet(styleSheet, rules)
}

const addContentTheme = (styleSheet, rgbaColor = 'rgba(0,0,0,0.05)') => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)

  const rules = [
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field  {
      background-color: ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-matrix-table  {
      background-color: ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-product-input {
      background-color: ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field .score-input-formz > label:hover {
      color: ${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field .score-input-formz > input:checked + label {
    color: ${color.toHexString()};
  }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .Select  {
      background-color:${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-radio svg{
      fill: transparent;
      transition: 0.4s;
      stroke-width: 15px;
     }`,
    `#main-form .formz-phone-Authentication-input .input-wrapper {
      background-color: ${color.toHex8String()}
    }`,
    `#main-form .formz-phone-Authentication-input .input-wrapper .verification-send-code  {
      color: ${color.toHex8String()}
    }`,
    `#main-form .email-verification-input .verification-send-code  {
      color: ${color.toHex8String()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-radio input[type='radio']:checked + .formz-radio-label:before {
      box-shadow: inset 0 0 0 3px ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-radio input[type='radio'] + .formz-radio-label:before {
      background: ${color.toHexString()}
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

const addButtonTheme = (styleSheet, rgbaColor) => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)

  const rules = [
    `#main-form .dashboard-submit-wrapper .formz-button-submit {
      background-color: ${color.toHexString()};
    }`,
    `#main-form .dashboard-submit-wrapper .formz-button-submit:hover {
      background-color: ${color.darken(10).toHexString()};
    }`,
    `#main-form .formz-button {
      background-color: ${color.toHexString()};
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

const addButtonTextTheme = (styleSheet, rgbaColor) => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)

  const rules = [
    `#main-form .dashboard-submit-wrapper .formz-button-submit {
      color: ${color.toHexString()};
    }`,
    `#main-form .formz-button {
      color: ${color.toHexString()};
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

const addTextTheme = (styleSheet, rgbaColor) => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)
  const lighten = tinycolor(rgbaColor).lighten(10).toHex8String()
  const darken = tinycolor(rgbaColor).darken(20).toHex8String()
  const transparent = tinycolor(rgbaColor).setAlpha(0.5).toHex8String()

  const rules = [
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field  {
      color: ${color.toHexString()};
    }`,
    `#main-form .form-maker-form-desc {
      color: ${color.toHexString()};
    }`,
    `#main-form .form-maker-form-title {
      color: ${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-section-input {
      color: ${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-section-input a{
      color: ${color.toHexString()};
      text-decoration: underline;
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-title {
      color: ${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-field-description {
      color: ${lighten};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-video-input-placeholder {
      color: ${lighten};
    }`,
    `#main-form .form-maker-form-fields .single-form-field-adminOnly {
      color: ${darken};
    }`,
    `#main-form .dashboard-formz-form-row .wrapper-class  {
      border: 1px solid ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .toolbar-class  {
      border: none;
      border-bottom: 1px solid ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .editor-class a {
      color: ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-matrix-table  {
      color: ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .max-min-message  {
      color: ${color.toHex8String()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field .score-input-formz > label:hover {
      background: ${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field .score-input-formz > input:checked + label {
    background: ${color.toHexString()};
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field  label:hover svg{
    fill(: ${color.toHex8String()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field input:checked + label svg{
    fill: ${color.toHex8String()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field .formz-rating-icon{
      fill: ${color.toHex8String()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-radio svg{
   stroke: ${color.toHex8String()}
     }`,
    `#main-form .dashboard-formz-form-row .starInput-empty{
  fill: ${color.toHex8String()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-input-pageBreak .formz-pageBreak-icon{
      border-color: ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .starInput-filled{
  fill: ${color.toHex8String()}
}`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .Select-placeholder  {
      color:${color.toHexString()};
  }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .Select .Select-clear {
    color:${color.toHexString()};
}`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .css-tlfecz-indicatorContainer {
    color:${color.toHexString()};
}`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .Select .css-g1d714-ValueContainer * {
    color:${color.toHexString()};
}`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .Select .Select-arrow-zone #selectArrow {
    fill:${color.toHexString()};
  }`,
    `#main-form .formz-phone-Authentication-input .input-wrapper .verification-send-code  {
    background-color: ${color.toHex8String()}
  }`,
    `#main-form .email-verification-input .verification-send-code  {
    background-color: ${color.toHex8String()}
  }`,
    `#main-form .formz-phone-Authentication-input .authentication-phone-field {
    color: ${color.toHex8String()}
  }`,
    `#main-form .dashboard-formz-form-row .formz-product-title{
    color: ${color.toHexString()};
  }`,
    `#main-form .dashboard-formz-form-row .formz-product-price{
    color: ${color.toHexString()};
  }`,
    `#main-form .dashboard-formz-form-row .formz-product-quantity-select, #main-form .dashboard-formz-form-row .formz-product-quantity-select svg{
    color: ${color.toHexString()};
  }`,
    `#main-form .dashboard-formz-form-row .formz-product-quantity-select fieldset {
    border-color: ${color.toHexString()};
  }`,
    `#main-form .dashboard-formz-form-row .formz-product-description{
    color: ${lighten};

  }`,
    `#main-form .dashboard-formz-form-row .formz-product-quantity{
    color: ${lighten};
  }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-field-table-text-choice {
      color:${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-field-table-checkbox-choice .checkMark::after{
      border-color: ${transparent}
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-field-table-checkbox-choice .checkMark{
      border-color: ${transparent}
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-field-table-checkbox-choice  input:checked ~ .checkMark::after{
      border-color: ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-field-table-checkbox-choice  input:checked ~ .checkMark{
      border-color: ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .choice-other-option-input{
      color: ${color.toHexString()}
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

const addBorderTheme = (styleSheet, rgbaColor) => {
  if (!styleSheet) return

  const color = tinycolor(rgbaColor)

  const rules = [
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-input-field  {
      border : 1px solid ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .Select  {
      border : 1px solid ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-field-and-label-holder .formz-section-input {
      border : 1px solid ${color.toHexString()}
    }`,
    `#main-form .formz-phone-Authentication-input .input-wrapper {
      border : 1px solid ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-product-input {
      border : 1px solid ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .formz-advance-matrix-row .formz-field-table-text-choice {
      border :1px solid ${color.toHexString()}
    }`,
    `#main-form .dashboard-formz-form-row .choice-other-option-input{
      border-bottom: 1px solid ${color.toHexString()}
    }`,
    `#main-form .form-border-theme{
      border-color: ${color.toHexString()}
    }`,
  ]

  applyStylesToStyleSheet(styleSheet, rules)
}

export {
  omitLinksInForm,
  addGeneralTheme,
  addContentTheme,
  addButtonTheme,
  addTextTheme,
  addBorderTheme,
  addBackgroundImage,
  addBackgroundShadow,
  addGeneralFont,
  addButtonTextTheme,
}
